'use client';

import { useState } from 'react';
import Icon, { type IconName } from './Icon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { cn } from '~/utils';
import type { ClassNameValue } from 'tailwind-merge';
import { cx } from 'class-variance-authority';

type LabelPosition = 'top' | 'left';
export default function InputSelect<T extends Record<string, any>>({
    value,
    hasError = false,
    disabled,
    placeholder,
    compact = false,
    labelPosition = 'top',
    options,
    iconBefore,
    iconAfter,
    onChange,
    label,
    labelClassName,
    valueField = 'value',
    labelField = 'label',
    className,
    id,
    open = false,
    search = false,
    readonly = false,
    variant = 'default',
}: {
    className?: ClassNameValue;
    hasError?: boolean;
    disabled?: boolean;
    onChange: (value: string) => void;
    options: T[] | readonly T[];
    value?: string | null;
    label?: React.ReactNode;
    labelClassName?: ClassNameValue;
    labelPosition?: LabelPosition;
    placeholder?: string;
    compact?: boolean;
    /** @default 'value' */
    valueField?: keyof T;
    /** @default 'label' */
    labelField?: keyof T;
    iconBefore?: IconName;
    iconAfter?: IconName;
    id?: string;
    open?: boolean;
    search?: boolean;
    /** @default false */
    readonly?: boolean;
    /** @default 'default' */
    variant?: 'default' | 'minimal';
}) {
    const [searchValue, setSearchValue] = useState('');
    const selectedOption =
        value == null
            ? undefined
            : options?.find(option => option[valueField].toString() === value.toString());
    // When the chosen option carries an icon, that icon IS the control's icon:
    // the trigger led with a generic one and then repeated the option's own in
    // front of the label, so the row read with two marks for one choice.
    const triggerIcon = Icon.isIconName(selectedOption?.icon) ? selectedOption.icon : iconBefore;
    const filteredOptions =
        search !== true || searchValue === ''
            ? options
            : options.filter(option =>
                  option[labelField].toLowerCase().includes(searchValue.toLowerCase()),
              );

    return (
        <Select
            value={value ?? undefined}
            onValueChange={onChange}
            disabled={disabled || false}
            open={readonly ? false : open ? true : undefined}
        >
            <SelectTrigger
                id={id}
                data-testid={id}
                readonly={readonly}
                className={cn(
                    'justify-start gap-2 text-sm',
                    variant === 'minimal'
                        ? 'inline-flex h-auto min-h-0 w-auto min-w-0 max-w-full justify-start gap-1 border-0 bg-transparent py-0 pl-2 pr-1 text-xs ring-0 transition-all hover:bg-gray-100 hover:ring-0 focus:ring-0 [&>svg:last-child]:size-4 [&>svg:last-child]:opacity-50'
                        : compact
                          ? 'pl-5 pr-2'
                          : 'h-12',
                    hasError && 'text-orange-700 ring-1 ring-orange-700',
                    className,
                )}
            >
                {triggerIcon && (
                    <Icon name={triggerIcon} className="text-blue-500" aria-hidden="true" />
                )}
                <div
                    className={cx(
                        'flex min-w-0 max-w-full',
                        label && labelPosition === 'top' && 'flex-col items-start',
                        label && labelPosition === 'left' && 'items-center gap-2',
                        !label && 'items-center',
                    )}
                >
                    {label && (
                        <span className={cn('text-sm font-medium text-secondary', labelClassName)}>
                            {label}
                        </span>
                    )}
                    <SelectValue
                        className={cn('block min-w-0 max-w-full truncate whitespace-nowrap')}
                        placeholder={
                            <span className="font-normal text-blue-500">{placeholder}</span>
                        }
                    >
                        {/* This allows us to show the selection instantly, without waiting for the options to be loaded */}
                        {/* Rendered here rather than left to Radix so the selection
                            shows before the options have loaded. */}
                        {!!selectedOption && (
                            <span className="truncate">{selectedOption[labelField]}</span>
                        )}
                    </SelectValue>
                </div>
                {iconAfter && <Icon name={iconAfter} className="text-blue-500" />}
            </SelectTrigger>
            <SelectContent className="max-h-60" data-testid={id ? `${id}-listbox` : undefined}>
                {search && (
                    <input
                        type="text"
                        className="w-full p-2"
                        placeholder="Search..."
                        value={searchValue}
                        onChange={e => setSearchValue(e.target.value)}
                    />
                )}
                {filteredOptions.map((option: any) => (
                    <SelectItem
                        key={option[valueField]}
                        value={option[valueField]}
                        onClick={() => {
                            onChange(option[valueField]);
                            setSearchValue('');
                        }}
                    >
                        <span className="flex min-w-0 items-center gap-2">
                            {Icon.isIconName(option.icon) && (
                                <Icon
                                    name={option.icon}
                                    size="sm"
                                    className="shrink-0 text-blue-500"
                                    aria-hidden="true"
                                />
                            )}
                            <span className="truncate">{option[labelField]}</span>
                        </span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
